import { useMemo } from 'react';
import {
  HandlersClinResponse,
  useDeleteApiClinsByIdMutation,
  useGetApiClinsQuery,
  useGetApiProgramsQuery,
  usePatchApiClinsByIdMutation,
  usePostApiClinsMutation,
} from '../../../../app/athenaApiGen';
import AdminCatalogPanel, { AdminCatalogSelectOption } from '../AdminCatalogPanel';

// CLIN master catalog (Pattern A). FK to Program is required on create and
// immutable on update. Deletes propagate to ProjectCLIN / DeltekProjectCLIN /
// DeltekProjectProjectCLIN — server returns 409 if any of those still
// reference the row.
//
// HandlersCreateClinRequest requires { clinCode, programId }. funded and
// projectAllocated are stored as numbers server-side; we send them as
// numbers when the field is non-empty.
const AdminCLINsPanel = () => {
  // Program FK options. The Programs list endpoint returns the array
  // directly (not enveloped), so we map it inline.
  const { data: programs } = useGetApiProgramsQuery();
  const programOptions = useMemo<AdminCatalogSelectOption[]>(
    () =>
      (programs ?? []).map((p) => ({
        value: p.programId ?? '',
        label: p.contractCode
          ? `${p.title ?? '(untitled)'} — ${p.contractCode}`
          : (p.title ?? p.programId ?? ''),
      })),
    [programs],
  );

  return (
    <AdminCatalogPanel<HandlersClinResponse>
      tableName="CLIN"
      apiPath="/api/clins"
      pkAccessor="clinId"
      useList={() => useGetApiClinsQuery({})}
      useCreate={usePostApiClinsMutation}
      useUpdate={usePatchApiClinsByIdMutation}
      useDelete={useDeleteApiClinsByIdMutation}
      buildCreateArg={(v) => ({
        handlersCreateClinRequest: {
          clinCode: (v.clinCode as string) ?? '',
          programId: (v.programId as string) ?? '',
          clinTitle: (v.clinTitle as string) || undefined,
          description: (v.description as string) || undefined,
          clinPeriod: (v.clinPeriod as string) || undefined,
          popStartDate: (v.popStartDate as string) || undefined,
          popEndDate: (v.popEndDate as string) || undefined,
          funded: v.funded ? Number(v.funded) : undefined,
          projectAllocated: v.projectAllocated
            ? Number(v.projectAllocated)
            : undefined,
        },
      })}
      buildUpdateArg={(row, v) => ({
        id: row.clinId as string,
        handlersUpdateClinRequest: {
          clinTitle: (v.clinTitle as string) || undefined,
          description: (v.description as string) || undefined,
          clinPeriod: (v.clinPeriod as string) || undefined,
          popStartDate: (v.popStartDate as string) || undefined,
          popEndDate: (v.popEndDate as string) || undefined,
          funded: v.funded ? Number(v.funded) : undefined,
          projectAllocated: v.projectAllocated
            ? Number(v.projectAllocated)
            : undefined,
        },
      })}
      buildDeleteArg={(row) => ({ id: row.clinId as string })}
      fields={[
        {
          name: 'clinCode',
          label: 'CLIN Code',
          type: 'text',
          required: true,
          immutableOnEdit: true,
          helperText: 'Immutable after creation.',
        },
        {
          name: 'programId',
          label: 'Program',
          type: 'select',
          required: true,
          immutableOnEdit: true,
          options: programOptions,
          helperText: 'Owning program. Immutable after creation.',
        },
        { name: 'clinTitle', label: 'Title', type: 'text' },
        { name: 'description', label: 'Description', type: 'textarea' },
        { name: 'clinPeriod', label: 'Period', type: 'text' },
        {
          name: 'popStartDate',
          label: 'PoP Start',
          type: 'text',
          helperText: 'YYYY-MM-DD',
        },
        {
          name: 'popEndDate',
          label: 'PoP End',
          type: 'text',
          helperText: 'YYYY-MM-DD',
        },
        {
          name: 'funded',
          label: 'Funded (USD)',
          type: 'text',
          helperText: 'Numeric; no $ or commas.',
        },
        {
          name: 'projectAllocated',
          label: 'Project Allocated (USD)',
          type: 'text',
          helperText: 'Numeric; no $ or commas.',
        },
      ]}
      fkConflictMessage="One or more ProjectCLIN, DeltekProjectCLIN, or DeltekProjectProjectCLIN rows still reference this CLIN. Detach those associations before deleting."
    />
  );
};

export default AdminCLINsPanel;
